import { useMemo, useState } from "react";
import {
  FaHome,
  FaPercentage,
  FaCalendarAlt,
  FaCalculator,
  FaChartLine,
  FaShieldAlt,
  FaAward,
  FaMapMarkerAlt,
  FaHeadset,
  FaWhatsapp,
  FaRegCalendarCheck,
  FaInfoCircle,
  FaFileAlt,
  FaUsers,
  FaCoins,
} from "react-icons/fa";

import "./Hipoteca.css";

function Hipoteca() {
  const [precio, setPrecio] = useState(130000);
  const [inicialPorcentaje, setInicialPorcentaje] = useState(20);
  const [interesAnual, setInteresAnual] = useState(7);
  const [plazo, setPlazo] = useState(20);

  const resultados = useMemo(() => {
    const precioNumerico = Math.max(Number(precio) || 0, 0);
    const inicialNumerica = Math.min(
      Math.max(Number(inicialPorcentaje) || 0, 0),
      100
    );
    const interesNumerico = Math.max(Number(interesAnual) || 0, 0);
    const plazoNumerico = Math.max(Number(plazo) || 1, 1);

    const montoInicial =
      precioNumerico * (inicialNumerica / 100);

    const montoFinanciado = Math.max(
      precioNumerico - montoInicial,
      0
    );

    const cantidadMeses = plazoNumerico * 12;
    const interesMensual = interesNumerico / 100 / 12;

    let cuotaMensual = 0;

    if (montoFinanciado > 0 && cantidadMeses > 0) {
      if (interesMensual === 0) {
        cuotaMensual = montoFinanciado / cantidadMeses;
      } else {
        const factor = Math.pow(
          1 + interesMensual,
          cantidadMeses
        );

        cuotaMensual =
          montoFinanciado *
          ((interesMensual * factor) / (factor - 1));
      }
    }

    const porcentajeFinanciado = Math.max(
      100 - inicialNumerica,
      0
    );

    return {
      montoInicial,
      montoFinanciado,
      cuotaMensual,
      porcentajeFinanciado,
    };
  }, [precio, inicialPorcentaje, interesAnual, plazo]);

  const formatearMoneda = (cantidad) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(Number(cantidad) || 0);

  const formatearCuota = (cantidad) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(Number(cantidad) || 0);

  /*
    República Dominicana:
    Código del país: 1
    Número: 8299623399
  */
  const whatsappNumero = "18299623399";

  const mensajeWhatsapp = encodeURIComponent(
    `Hola, equipo de YANNA A&P CONSTRUCTIONS.

Estoy interesado(a) en recibir asesoría sobre uno de sus apartamentos.

Detalles de Interes:

• Precio de la propiedad: ${formatearMoneda(precio)}
• Inicial estimada (${inicialPorcentaje}%): ${formatearMoneda(
      resultados.montoInicial
    )}
• Monto financiado: ${formatearMoneda(
      resultados.montoFinanciado
    )}
• Tasa de interés anual: ${Number(interesAnual).toFixed(2)}%
• Plazo del financiamiento: ${plazo} años
• Cuota mensual estimada: ${formatearCuota(
      resultados.cuotaMensual
    )}

Agradecería información sobre la disponibilidad de los apartamentos, los requisitos de financiamiento y la posibilidad de coordinar una visita al proyecto.

Quedo atento(a). Muchas gracias.`
  );

  const porcentajeGrafico = Math.min(
    Math.max(Number(inicialPorcentaje) || 0, 0),
    100
  );

  const estiloGrafico = {
    background: `conic-gradient(
      #0f304e 0% ${porcentajeGrafico}%,
      #d49732 ${porcentajeGrafico}% 100%
    )`,
  };

  return (
    <section className="hipoteca" id="hipoteca">
      <div className="hipoteca-container">

        {/* COLUMNA IZQUIERDA */}

        <article className="hipoteca-panel hipoteca-calculadora">
          <div className="hipoteca-encabezado">
            <span className="hipoteca-etiqueta">
              PLANIFICA TU INVERSIÓN
            </span>

            <h2>Calculadora Hipotecaria</h2>

            <span className="hipoteca-linea"></span>

            <p>
              Simula tu financiamiento y conoce tu cuota mensual
              estimada de manera rápida y sencilla.
            </p>
          </div>

          <div className="hipoteca-campos">

            {/* PRECIO */}

            <div className="hipoteca-campo">
              <div className="hipoteca-icono">
                <FaHome />
              </div>

              <div className="hipoteca-control">
                <label htmlFor="precio">
                  Precio de la propiedad
                </label>

                <div className="hipoteca-input-contenedor">
                  <span>US$</span>

                  <input
                    id="precio"
                    type="number"
                    min="0"
                    step="1000"
                    value={precio}
                    onChange={(evento) =>
                      setPrecio(evento.target.value)
                    }
                    aria-label="Precio de la propiedad"
                  />
                </div>
              </div>
            </div>

            {/* INICIAL */}

            <div className="hipoteca-campo">
              <div className="hipoteca-icono">
                <FaPercentage />
              </div>

              <div className="hipoteca-control">
                <div className="hipoteca-label-fila">
                  <label htmlFor="inicial">Inicial</label>

                  <strong>
                    {formatearMoneda(resultados.montoInicial)}
                  </strong>
                </div>

                <div className="hipoteca-rango-fila">
                  <div className="hipoteca-porcentaje">
                    {inicialPorcentaje}%
                  </div>

                  <input
                    id="inicial"
                    className="hipoteca-range"
                    type="range"
                    min="0"
                    max="80"
                    step="1"
                    value={inicialPorcentaje}
                    onChange={(evento) =>
                      setInicialPorcentaje(
                        Number(evento.target.value)
                      )
                    }
                    aria-label="Porcentaje de inicial"
                  />
                </div>
              </div>
            </div>

            {/* INTERÉS */}

            <div className="hipoteca-campo">
              <div className="hipoteca-icono">
                <FaPercentage />
              </div>

              <div className="hipoteca-control">
                <label htmlFor="interes">
                  Interés anual
                </label>

                <div className="hipoteca-input-contenedor">
                  <input
                    id="interes"
                    type="number"
                    min="0"
                    max="40"
                    step="0.1"
                    value={interesAnual}
                    onChange={(evento) =>
                      setInteresAnual(evento.target.value)
                    }
                    aria-label="Interés anual"
                  />

                  <span>%</span>
                </div>
              </div>
            </div>

            {/* PLAZO */}

            <div className="hipoteca-campo">
              <div className="hipoteca-icono">
                <FaCalendarAlt />
              </div>

              <div className="hipoteca-control">
                <label htmlFor="plazo">
                  Plazo del financiamiento
                </label>

                <select
                  id="plazo"
                  value={plazo}
                  onChange={(evento) =>
                    setPlazo(Number(evento.target.value))
                  }
                >
                  <option value={5}>5 años</option>
                  <option value={10}>10 años</option>
                  <option value={15}>15 años</option>
                  <option value={20}>20 años</option>
                  <option value={25}>25 años</option>
                  <option value={30}>30 años</option>
                </select>
              </div>
            </div>
          </div>

          {/* RESULTADO PRINCIPAL */}

          <div className="hipoteca-resultado">
            <div className="hipoteca-resultado-icono">
              <FaCalculator />
            </div>

            <div className="hipoteca-resultado-info">
              <span>Cuota mensual estimada</span>

              <strong>
                {formatearCuota(resultados.cuotaMensual)}
              </strong>

              <small>
                Basado en financiamiento a {plazo} años
              </small>
            </div>
          </div>

          {/* BOTONES */}

          <div className="hipoteca-acciones">
            <a
              href={`https://wa.me/${whatsappNumero}?text=${mensajeWhatsapp}`}
              target="_blank"
              rel="noopener noreferrer"
              className="hipoteca-boton hipoteca-boton-oscuro"
            >
              <FaWhatsapp />
              Solicitar asesoría
            </a>

            <a
              href="#contacto"
              className="hipoteca-boton hipoteca-boton-dorado"
            >
              <FaRegCalendarCheck />
              Agendar una visita
            </a>
          </div>

          <p className="hipoteca-aviso">
            <FaInfoCircle />

            <span>
              Este cálculo es referencial. Las tasas y condiciones
              finales dependen de la entidad financiera.
            </span>
          </p>
        </article>

        {/* COLUMNA DERECHA */}

        <article className="hipoteca-panel hipoteca-resumen">
          <div className="hipoteca-encabezado">
            <span className="hipoteca-etiqueta">
              DETALLE FINANCIERO
            </span>

            <h2>Resumen de tu inversión</h2>

            <span className="hipoteca-linea"></span>

            <p>
              Conoce el detalle estimado de tu financiamiento.
            </p>
          </div>

          {/* TABLA */}

          <div className="hipoteca-tabla">
            <div className="hipoteca-tabla-fila">
              <div className="hipoteca-tabla-concepto">
                <span className="hipoteca-tabla-icono">
                  <FaHome />
                </span>

                <span>Precio de la propiedad</span>
              </div>

              <strong>{formatearMoneda(precio)}</strong>
            </div>

            <div className="hipoteca-tabla-fila">
              <div className="hipoteca-tabla-concepto">
                <span className="hipoteca-tabla-icono">
                  <FaPercentage />
                </span>

                <span>Inicial ({inicialPorcentaje}%)</span>
              </div>

              <strong>
                {formatearMoneda(resultados.montoInicial)}
              </strong>
            </div>

            <div className="hipoteca-tabla-fila">
              <div className="hipoteca-tabla-concepto">
                <span className="hipoteca-tabla-icono">
                  <FaCoins />
                </span>

                <span>Monto financiado</span>
              </div>

              <strong>
                {formatearMoneda(resultados.montoFinanciado)}
              </strong>
            </div>

            <div className="hipoteca-tabla-fila">
              <div className="hipoteca-tabla-concepto">
                <span className="hipoteca-tabla-icono">
                  <FaChartLine />
                </span>

                <span>Interés anual</span>
              </div>

              <strong>
                {Number(interesAnual || 0).toFixed(2)}%
              </strong>
            </div>

            <div className="hipoteca-tabla-fila">
              <div className="hipoteca-tabla-concepto">
                <span className="hipoteca-tabla-icono">
                  <FaCalendarAlt />
                </span>

                <span>Plazo</span>
              </div>

              <strong>{plazo} años</strong>
            </div>

            <div className="hipoteca-tabla-fila hipoteca-tabla-total">
              <span>Cuota mensual aproximada</span>

              <strong>
                {formatearCuota(resultados.cuotaMensual)}
              </strong>
            </div>
          </div>

          {/* PARTE INFERIOR DEL RESUMEN */}

          <div className="hipoteca-resumen-inferior">

            {/* GRÁFICO CIRCULAR */}

            <div className="hipoteca-distribucion">
              <h3>Distribución de tu inversión</h3>

              <div
                className="hipoteca-grafico"
                style={estiloGrafico}
              >
                <div className="hipoteca-grafico-centro">
                  <span>US$</span>

                  <strong>
                    {new Intl.NumberFormat("en-US", {
                      maximumFractionDigits: 0,
                    }).format(Number(precio) || 0)}
                  </strong>
                </div>
              </div>

              <div className="hipoteca-leyenda">
                <div className="hipoteca-leyenda-fila">
                  <span className="hipoteca-punto hipoteca-punto-inicial"></span>

                  <p>Inicial ({inicialPorcentaje}%)</p>

                  <strong>
                    {formatearMoneda(resultados.montoInicial)}
                  </strong>
                </div>

                <div className="hipoteca-leyenda-fila">
                  <span className="hipoteca-punto hipoteca-punto-financiado"></span>

                  <p>
                    Financiado ({resultados.porcentajeFinanciado}%)
                  </p>

                  <strong>
                    {formatearMoneda(
                      resultados.montoFinanciado
                    )}
                  </strong>
                </div>
              </div>
            </div>

            {/* BENEFICIOS */}

            <div className="hipoteca-beneficios">
              <div className="hipoteca-beneficios-titulo">
                <h3>¿Por qué invertir aquí?</h3>
                <span></span>
              </div>

              <div className="hipoteca-beneficio">
                <div>
                  <FaChartLine />
                </div>

                <section>
                  <h4>Alta valorización</h4>
                  <p>
                    Zona en constante crecimiento y alta demanda.
                  </p>
                </section>
              </div>

              <div className="hipoteca-beneficio">
                <div>
                  <FaShieldAlt />
                </div>

                <section>
                  <h4>Financiamiento disponible</h4>
                  <p>
                    Opciones con las principales entidades
                    financieras del país.
                  </p>
                </section>
              </div>

              <div className="hipoteca-beneficio">
                <div>
                  <FaAward />
                </div>

                <section>
                  <h4>Entrega garantizada</h4>
                  <p>
                    Construimos con altos estándares de calidad.
                  </p>
                </section>
              </div>

              <div className="hipoteca-beneficio">
                <div>
                  <FaMapMarkerAlt />
                </div>

                <section>
                  <h4>Ubicación privilegiada</h4>
                  <p>
                    En Madre Vieja Sur, cerca de todo lo necesario.
                  </p>
                </section>
              </div>

              <div className="hipoteca-beneficio">
                <div>
                  <FaHeadset />
                </div>

                <section>
                  <h4>Asesoría personalizada</h4>
                  <p>
                    Acompañamiento profesional durante el proceso.
                  </p>
                </section>
              </div>
            </div>
          </div>
        </article>
      </div>

      {/* BARRA INFERIOR */}

      <div className="hipoteca-seguridad">
        <div className="hipoteca-seguridad-item hipoteca-seguridad-principal">
          <div className="hipoteca-seguridad-icono">
            <FaShieldAlt />
          </div>

          <div>
            <strong>Tu inversión está protegida</strong>
            <span>
              Seguridad jurídica y respaldo en cada proyecto.
            </span>
          </div>
        </div>

        <div className="hipoteca-seguridad-item">
          <div className="hipoteca-seguridad-icono">
            <FaFileAlt />
          </div>

          <div>
            <strong>Documentación al día</strong>
            <span>
              Proyectos con los permisos y certificaciones
              correspondientes.
            </span>
          </div>
        </div>

        <div className="hipoteca-seguridad-item">
          <div className="hipoteca-seguridad-icono">
            <FaAward />
          </div>

          <div>
            <strong>Construcción de calidad</strong>
            <span>
              Materiales y procesos de primera para tu tranquilidad.
            </span>
          </div>
        </div>

        <div className="hipoteca-seguridad-item">
          <div className="hipoteca-seguridad-icono">
            <FaUsers />
          </div>

          <div>
            <strong>Respaldo profesional</strong>
            <span>
              Un equipo preparado para acompañarte en cada paso.
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}

export default Hipoteca;